import { Scan, Plus, Eye, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MicrochipData } from './MicrochipLinking';

interface MicrochipStatusCardProps {
  petName: string;
  chipData?: (MicrochipData & { linkedDate: string }) | null;
  onLink: () => void;
  onManage: () => void;
}

export function MicrochipStatusCard({
  petName,
  chipData,
  onLink,
  onManage,
}: MicrochipStatusCardProps) {
  const { t } = useTranslation();
  const maskChipNumber = (number: string): string => {
    if (number.length <= 4) return number;
    const visible = number.slice(-4);
    const masked = '•'.repeat(number.length - 4);
    return masked + visible;
  };

  // State 1: Not Linked
  if (!chipData) {
    return (
      <div className="bg-white dark:bg-[#16151D] border-2 border-gray-200 dark:border-white/8 rounded-xl p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Scan className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3
                className="font-semibold mb-1 text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                {t('petOverview.microchipCard.titleOptional')}
              </h3>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                <span
                  className="text-sm text-gray-600 dark:text-white/70"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {t('petOverview.microchipCard.noMicrochipLinked')}
                </span>
              </div>
            </div>
          </div>
        </div>

        <p
          className="text-sm mb-4 text-gray-600 dark:text-white/70"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('petOverview.microchipCard.ifPetHasMicrochip', { petName })}
        </p>

        <button
          onClick={onLink}
          className="w-full px-4 py-3 rounded-xl text-white hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
          }}
        >
          <Plus className="w-5 h-5" />
          {t('petOverview.microchipCard.linkMicrochipOptional')}
        </button>

        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/30 rounded-lg p-3">
          <p
            className="text-xs text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <strong className="text-gray-900 dark:text-blue-300">
              {t('petOverview.microchipCard.note')}
            </strong>{' '}
            {t('petOverview.microchipCard.noteOptionalRef')}
          </p>
        </div>
      </div>
    );
  }

  // State 2: Linked
  return (
    <div className="bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 border-2 border-green-200 dark:border-green-700/30 rounded-xl p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
            <Scan className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3
              className="font-semibold mb-1 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              {t('petOverview.microchipCard.microchipLinked')}
            </h3>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400"></div>
              <span
                className="text-sm text-gray-600 dark:text-white/70"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {t('petOverview.active')}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Chip Details */}
      <div className="bg-white/60 dark:bg-white/5 rounded-lg p-4 mb-4 space-y-3">
        <div>
          <div
            className="text-xs mb-1 text-gray-600 dark:text-white/70"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {t('petOverview.microchipCard.microchipNumber')}
          </div>
          <div
            className="font-mono text-sm text-gray-900 dark:text-white"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
          >
            {maskChipNumber(chipData.chipNumber)}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <div
              className="mb-1 text-gray-600 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('petOverview.microchipCard.linkedOn')}
            </div>
            <div
              className="text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {new Date(chipData.linkedDate).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </div>
          </div>

          <div>
            <div
              className="mb-1 text-gray-600 dark:text-white/70"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('petOverview.microchipCard.visibility')}
            </div>
            <div
              className="flex items-center gap-1 text-gray-900 dark:text-white"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {chipData.visibility === 'only-me' && (
                <>
                  <Shield className="w-3 h-3" />
                  <span>{t('petOverview.microchipCard.onlyMe')}</span>
                </>
              )}
              {chipData.visibility === 'professionals' && (
                <>
                  <Eye className="w-3 h-3" />
                  <span>{t('petOverview.microchipCard.professionals')}</span>
                </>
              )}
              {chipData.visibility === 'emergency' && (
                <>
                  <Scan className="w-3 h-3" />
                  <span>{t('petOverview.microchipCard.emergency')}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {chipData.linkToDID && (
          <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400 pt-2 border-t border-green-200 dark:border-green-700/30">
            <div className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400"></div>
            <span style={{ fontFamily: 'Inter, sans-serif' }}>
              {t('petOverview.microchipCard.linkedToPetDid')}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={onManage}
          className="px-4 py-2 rounded-lg bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors text-sm border border-gray-200 dark:border-white/8 text-gray-600 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {t('petOverview.microchipCard.manage')}
        </button>
        <button
          onClick={onManage}
          className="px-4 py-2 rounded-lg text-white hover:opacity-90 transition-opacity text-sm"
          style={{
            background: 'linear-gradient(135deg, #936FEF 0%, #2D2F7E 100%)',
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
          }}
        >
          {t('petOverview.microchipCard.viewDetails')}
        </button>
      </div>
    </div>
  );
}
